import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as DashboardAPI from 'api/Dashboard';
import { unwrap, unwrapList, apiErrorMessage } from 'utils/notify';
import { addDaysYmd, browserTimeZone, dayBoundsIso, todayYmd } from 'utils/localDate';

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard store — super-admin dashboard cards (DASHBOARD_API.md).
//
//   tenantSignups : the "new workspaces" card                       (§1)
//   tenantsOnDay  : the workspaces behind one bar of that card      (§2)
//
// Every call sends the browser's timezone (or day boundaries computed in it),
// so the bars, the "today" number and the drill-down agree on what a day is.
//
// Each read keeps the requestId of its latest call: the range changes faster
// than responses arrive, and a superseded (or aborted) request must not
// overwrite the newer one's state.
// ─────────────────────────────────────────────────────────────────────────────

// Matches the server's cap on the card's range.
export const MAX_RANGE_DAYS = 366;

// The card leaves these out; the tenant list doesn't yet (§2 "Known gap").
const NON_WORKSPACE_SCHEMAS = ['public', 'template_schema'];

// Last 7 days, ending today.
export const defaultSignupFilters = () => {
  const today = todayYmd();
  return { date_from: addDaysYmd(today, -6), date_to: today, is_active: '' };
};

const initialState = {
  signupFilters: defaultSignupFilters(),
  tenantSignups: { data: null, loading: false, error: null, requestId: null },
  tenantsOnDay: { day: null, results: [], loading: false, error: null, requestId: null }
};

// ── thunks ──────────────────────────────────────────────────────────────────

const getTenantSignups = createAsyncThunk('dashboard/getTenantSignups', async (filters, { signal, rejectWithValue }) => {
  try {
    return unwrap(await DashboardAPI.getTenantSignups({ ...filters, tz: browserTimeZone() }, signal)) || null;
  } catch (error) {
    return rejectWithValue(apiErrorMessage(error, 'Failed to load new workspaces.'));
  }
});

// `day` is a "YYYY-MM-DD" from the card's `daily` rows.
const getTenantsOnDay = createAsyncThunk('dashboard/getTenantsOnDay', async ({ day, is_active }, { signal, rejectWithValue }) => {
  const [gte, lt] = dayBoundsIso(day);
  try {
    const res = await DashboardAPI.getTenantsCreated({ created_at__gte: gte, created_at__lt: lt, is_active, get_all: 1 }, signal);
    return unwrapList(res).results.filter((t) => !NON_WORKSPACE_SCHEMAS.includes(t.schema_name));
  } catch (error) {
    return rejectWithValue(apiErrorMessage(error, 'Failed to load workspaces for this day.'));
  }
});

// ── slice ───────────────────────────────────────────────────────────────────

const isLatest = (slot, action) => slot.requestId === action.meta.requestId;

const failureMessage = (action) => action.payload || action.error?.message || 'Request failed.';

const DashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setSignupFilters: (state, action) => {
      state.signupFilters = { ...state.signupFilters, ...action.payload };
    },
    resetSignupFilters: (state) => {
      state.signupFilters = defaultSignupFilters();
    },
    closeTenantsOnDay: (state) => {
      state.tenantsOnDay = initialState.tenantsOnDay;
    }
  },
  extraReducers: {
    [getTenantSignups.pending]: (state, action) => {
      state.tenantSignups.loading = true;
      state.tenantSignups.error = null;
      state.tenantSignups.requestId = action.meta.requestId;
    },
    [getTenantSignups.fulfilled]: (state, action) => {
      if (!isLatest(state.tenantSignups, action)) return;
      state.tenantSignups.loading = false;
      state.tenantSignups.data = action.payload;
    },
    [getTenantSignups.rejected]: (state, action) => {
      if (!isLatest(state.tenantSignups, action)) return;
      state.tenantSignups.loading = false;
      if (!action.meta.aborted) state.tenantSignups.error = failureMessage(action);
    },

    [getTenantsOnDay.pending]: (state, action) => {
      state.tenantsOnDay = { ...initialState.tenantsOnDay, day: action.meta.arg.day, loading: true, requestId: action.meta.requestId };
    },
    [getTenantsOnDay.fulfilled]: (state, action) => {
      if (!isLatest(state.tenantsOnDay, action)) return;
      state.tenantsOnDay.loading = false;
      state.tenantsOnDay.results = action.payload;
    },
    [getTenantsOnDay.rejected]: (state, action) => {
      if (!isLatest(state.tenantsOnDay, action)) return;
      state.tenantsOnDay.loading = false;
      if (!action.meta.aborted) state.tenantsOnDay.error = failureMessage(action);
    }
  }
});

const { setSignupFilters, resetSignupFilters, closeTenantsOnDay } = DashboardSlice.actions;

// ── selectors ───────────────────────────────────────────────────────────────

export const selectSignupFilters = (state) => state.dashboard.signupFilters;
export const selectTenantSignups = (state) => state.dashboard.tenantSignups;
export const selectTenantsOnDay = (state) => state.dashboard.tenantsOnDay;

export default DashboardSlice.reducer;

export { setSignupFilters, resetSignupFilters, closeTenantsOnDay, getTenantSignups, getTenantsOnDay };
